import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../WebSocketContext';

export default function useProjectChat(caseid, open, preferredConversationId) {
  const { sendMessage, subscribeToMessages, isConnected } = useWebSocket();
  const { i18n } = useTranslation();
  const preferred = useRef(preferredConversationId);
  const [enabled, setEnabled] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [rejectedMessage, setRejectedMessage] = useState(null);
  const [referenceCatalog, setReferenceCatalog] = useState({ tags: [], page: 0, has_more: false });
  const [progress, setProgress] = useState(null);
  const [recheckStatus, setRecheckStatus] = useState('idle');
  const recheckRequest = useRef(null);
  const selected = useRef(null);
  const prefix = useRef(`project-chat-${Math.random().toString(36).slice(2)}-`);
  const serial = useRef(0);
  const latestCapabilityRequest = useRef(null);
  const requests = useRef(new Map());
  const initialize = useRef(false);
  const refreshedJob = useRef(null);
  const selectionEpoch = useRef(0);
  const latestSnapshot = useRef(null);

  const send = useCallback((action, fields = {}, blocking = true, purpose = null) => {
    if (!sendMessage || !isConnected) return;
    const request_id = `${prefix.current}${++serial.current}`;
    if (action === 'capabilities') latestCapabilityRequest.current = request_id;
    requests.current.set(request_id, { action, blocking, purpose, at: Date.now(), sequence: serial.current,
      epoch: selectionEpoch.current, conversationId: fields.conversation_id,
      revision: fields.revision,
      messageText: action === 'message' ? fields.text : null,
      references: action === 'message' ? fields.references : null });
    if (purpose === 'recheck') {
      recheckRequest.current = request_id;
      setRecheckStatus('checking');
    }
    if (blocking) { setPending(true); setError(''); setRejectedMessage(null); }
    sendMessage({ command: 'projectChat', protocol: 2, caseid, action, request_id,
      language: i18n?.language || 'en', ...fields });
  }, [caseid, i18n?.language, isConnected, sendMessage]);

  useEffect(() => {
    if (!subscribeToMessages) return undefined;
    return subscribeToMessages((message) => {
      if (message.command === 'projectAssistantAvailabilityChanged') {
        send('capabilities', {}, false);
        return;
      }
      if (message.command !== 'projectChat' || String(message.data?.caseid) !== String(caseid)) return;
      const data = message.data;
      const request = requests.current.get(data.request_id);
      if (!request) return;
      const currentSelection = request.epoch === selectionEpoch.current;
      if (data.status === 'progress') {
        request.at = Date.now();
        if (currentSelection && selected.current === data.conversation_id &&
          latestSnapshot.current?.conversation.busy === data.request_id) setProgress(data.progress);
        return;
      }
      // Durable runs use the same request ID for their final response.
      if (!data.conversation?.busy || request.action === 'load') requests.current.delete(data.request_id);
      request.blocking = false;
      if (request.action === 'capabilities' && data.request_id !== latestCapabilityRequest.current) return;
      if (!currentSelection && request.action !== 'reference_catalog') return;
      if (request.purpose === 'recheck' && recheckRequest.current === data.request_id) {
        setRecheckStatus(data.status === 'error' || data.conversation?.id !== selected.current ? 'error' : 'checked');
      }
      setPending([...requests.current.values()].some(item => item.blocking && item.epoch === selectionEpoch.current));
      if (data.status === 'error') {
        setError(data.code || 'needs_review');
        if (data.code === 'disabled' || (request.action === 'capabilities' && data.code === 'access_denied')) setEnabled(false);
        if (request.messageText && request.conversationId === selected.current) {
          // The server explicitly rejected this turn before accepting it. Keep
          // the draft available; never retry a chat operation automatically.
          setRejectedMessage({ text: request.messageText, conversationId: request.conversationId,
            references: request.references,
            request_id: data.request_id });
        }
        if (data.code === 'access_denied') {
          setConversation(null);
          setHistory([]);
          selected.current = null;
        }
        if (data.code === 'conflict' && selected.current) {
          send('load', { conversation_id: selected.current }, false);
        }
        return;
      }
      if (typeof data.enabled === 'boolean') setEnabled(data.enabled);
      if (data.reference_catalog) setReferenceCatalog(previous => ({ ...data.reference_catalog,
        tags: [...new Map([...(data.reference_catalog.page === 1 ? [] : previous.tags), ...data.reference_catalog.tags]
          .map(tag => [`${tag.tag_type}:${tag.variable}`, tag])).values()] }));
      if (data.conversations) {
        setHistory(data.conversations);
        if (initialize.current) {
          initialize.current = false;
          selectionEpoch.current += 1;
          if (preferred.current || data.conversations.length) {
            selected.current = preferred.current || data.conversations[0].id;
            preferred.current = null;
            send('load', { conversation_id: selected.current });
          } else {
            send('new');
          }
        }
      }
      if (data.conversation) {
        const incoming = data.conversation;
        if (request.action === 'new') selected.current = incoming.id;
        if (selected.current !== incoming.id) return;
        const previous = latestSnapshot.current?.conversation;
        if (data.older && previous?.id === incoming.id) {
          const messages = [...incoming.messages, ...previous.messages];
          const merged = { ...previous, messages: [...new Map(messages.map(item => [item.id, item])).values()],
            has_older: incoming.has_older };
          latestSnapshot.current = { ...latestSnapshot.current, conversation: merged };
          setConversation(merged);
          return;
        }
        if (previous?.id === incoming.id) {
          if (previous.revision > incoming.revision ||
            (previous.revision === incoming.revision && previous.updated_at > incoming.updated_at)) return;
          if (previous.revision === incoming.revision && previous.updated_at === incoming.updated_at) {
            const oldJobTime = previous.state?.job?.updated_at || 0;
            const newJobTime = incoming.state?.job?.updated_at || 0;
            if (oldJobTime > newJobTime || (oldJobTime === newJobTime &&
              latestSnapshot.current.sequence > request.sequence)) return;
          }
        }
        if (request.action === 'load' && !incoming.busy) {
          // A completed command reply can be lost on disconnect. A newer,
          // authorized idle snapshot resolves its UI wait without replaying it.
          for (const [id, pendingRequest] of requests.current) {
            if (pendingRequest.epoch === selectionEpoch.current && pendingRequest.blocking &&
                pendingRequest.conversationId === incoming.id && Number.isInteger(pendingRequest.revision) &&
                incoming.revision > pendingRequest.revision) requests.current.delete(id);
          }
          setPending([...requests.current.values()].some(item => item.blocking && item.epoch === selectionEpoch.current));
        }
        latestSnapshot.current = { conversation: incoming, sequence: request.sequence };
        setHistory((previous) => {
          const existing = previous.find((item) => item.id === incoming.id);
          const state = incoming.state || {};
          const hasActivity = incoming.messages?.length || incoming.busy || incoming.title || state.goal ||
            state.job_id || state.job || state.receipts?.length || state.inflight;
          if (!hasActivity) return previous;
          const firstQuestion = incoming.messages?.find((item) => item.role === 'user' && item.content?.text?.trim());
          const label = incoming.title || state.goal || existing?.title || firstQuestion?.content.text || '';
          const title = Array.from(label.trim().replace(/\s+/gu, ' ')).slice(0, 120).join('');
          return [{ id: incoming.id, title, updated_at: incoming.updated_at },
            ...previous.filter((item) => item.id !== incoming.id)].slice(0, 50);
        });
        setConversation(incoming);
        setProgress(incoming.state?.progress || null);
        if (!incoming.busy && incoming.state?.receipts?.some((item) => item.target === 'widget')) {
          sendMessage({ command: 'getWidgets', caseid });
        }
        const job = incoming.state?.job;
        if (job?.widget_id && ['awaiting_final_review', 'completed'].includes(job.phase) && refreshedJob.current !== job.id) {
          refreshedJob.current = job.id;
          sendMessage({ command: 'getWidgets', caseid });
        }
      }
    });
  }, [caseid, send, sendMessage, subscribeToMessages]);

  useEffect(() => {
    selectionEpoch.current += 1;
    selected.current = null;
    latestSnapshot.current = null;
    requests.current.clear();
    initialize.current = false;
    refreshedJob.current = null;
    setEnabled(false);
    setConversation(null);
    setHistory([]);
    setPending(false);
    setProgress(null);
    setRecheckStatus('idle');
    recheckRequest.current = null;
    setError('');
    setRejectedMessage(null);
    setReferenceCatalog({ tags: [], page: 0, has_more: false });
  }, [caseid]);

  useEffect(() => {
    // Keep a previously authorized panel visible during a transient outage so
    // users can see connection recovery and retain their unsent draft.
    if (isConnected) send('capabilities', {}, false);
  }, [isConnected, send]);

  useEffect(() => {
    recheckRequest.current = null;
    setRecheckStatus('idle');
  }, [conversation?.state?.job?.id]);

  useEffect(() => {
    if (!open || !enabled || !isConnected) return;
    initialize.current = !selected.current;
    send('list', {}, false);
    send('reference_catalog', { page: 1 }, false);
    if (selected.current) send('load', { conversation_id: selected.current });
  }, [enabled, isConnected, open, send]);

  useEffect(() => {
    const activeJob = ['trial_queued', 'trial_running', 'queued', 'running', 'aggregating'].includes(conversation?.state?.job?.phase);
    if (!enabled || !open || !isConnected || !conversation?.id || (!conversation.busy && !activeJob && !pending)) return undefined;
    const interval = setInterval(() => send('load', { conversation_id: conversation.id }, false), 4000);
    return () => clearInterval(interval);
  }, [conversation?.busy, conversation?.state?.job?.phase, conversation?.id, enabled, isConnected, open, pending, send]);

  useEffect(() => {
    const interval = setInterval(() => {
      for (const [key, request] of requests.current) {
        if (Date.now() - request.at > (request.purpose === 'recheck' ? 30000 : 150000)) {
          requests.current.delete(key);
          if (request.purpose === 'recheck' && recheckRequest.current === key && request.epoch === selectionEpoch.current) {
            setRecheckStatus('error');
          }
          if (request.blocking && request.epoch === selectionEpoch.current) {
            setPending([...requests.current.values()].some(item => item.blocking && item.epoch === selectionEpoch.current));
            setError('connection');
            if (request.messageText) setRejectedMessage({ text: request.messageText,
              references: request.references, conversationId: request.conversationId });
          }
        }
      }
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const choose = (id) => {
    selectionEpoch.current += 1;
    selected.current = id;
    latestSnapshot.current = null;
    setConversation(null);
    setProgress(null);
    setRecheckStatus('idle');
    recheckRequest.current = null;
    setError('');
    setRejectedMessage(null);
    send('load', { conversation_id: id });
  };
  const act = (action, fields = {}) => {
    if (!conversation) return;
    send(action, { conversation_id: conversation.id, revision: conversation.revision,
      preview_id: conversation.state?.preview?.id, decision_id: conversation.state?.workflow?.decision_id, ...fields });
  };
  return { enabled, conversation, history, pending, error, rejectedMessage, progress, recheckStatus, referenceCatalog,
    loadMoreTags: () => send('reference_catalog', { page: referenceCatalog.page + 1 }, false), connected: Boolean(isConnected),
    choose, act, newConversation: () => {
      selectionEpoch.current += 1;
      selected.current = null;
      latestSnapshot.current = null;
      setConversation(null);
      setProgress(null);
      setError('');
      setRejectedMessage(null);
      setRecheckStatus('idle');
      recheckRequest.current = null;
      send('new');
    },
    refresh: () => conversation && send('load', { conversation_id: conversation.id }, false, 'recheck'),
    loadOlder: () => send('load', { conversation_id: conversation.id, before: conversation.messages[0].id }) };
}
